import React, {useMemo, useState} from 'react';
import {Table, Box, Header, Input, Button, SpaceBetween, Popover} from '@amzn/awsui-components-react';

interface WeeklyData {
    [key: string]: number;
}

interface TableRow {
    metric: string;
    [key: string]:
        | string
        | number
        | {
              value: number;
              groupName: string;
              weekIndex: number;
              features: string[];
              isModelError?: boolean;
              inSampleForecast?: number;
              actualValue?: number;
              isPostProcess?: boolean;
              dailyValues?: number[];
              step?: string;
          };
}

const formatNumber = (num: number): string => {
    return Math.round(num).toLocaleString();
};

export default function LgbmExplainerTable({
    weeklyData,
    pastWeekDataArray,
    featureMapping,
    pastWeekDates,
    setPastWeekDates,
    forecastIdsMetadata,
    selectedForecastId,
    setModalVisible,
    setModalData,
    hasInitialLoad = false,
}: {
    weeklyData: WeeklyData[];
    pastWeekDataArray: (WeeklyData | null)[];
    featureMapping: {[key: string]: string[]};
    pastWeekDates: string[];
    setPastWeekDates: (dates: string[]) => void;
    forecastIdsMetadata?: any;
    selectedForecastId?: string;
    setModalVisible: (visible: boolean) => void;
    setModalData: (data: any) => void;
    hasInitialLoad?: boolean;
}) {
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverData, setPopoverData] = useState<{groupName: string; weekIndex: number; features: string[]} | null>(null);
    const tableData: TableRow[] = useMemo(() => {
        const weeks = Array.from({length: 5}, (_, index) => `Week ${index + 1}`);
        const rows: TableRow[] = [];

        // Week start date row
        const weekStartRow: TableRow = {metric: 'Week Start Date'};
        weeks.forEach((week, index) => {
            const selectedForecast = forecastIdsMetadata?.find((f: any) => f.forecastId === selectedForecastId);
            const forecastStartDate = new Date(selectedForecast?.forecastStartDate || new Date());

            // Find the Sunday of the forecast start date
            const firstSunday = new Date(forecastStartDate);
            firstSunday.setDate(forecastStartDate.getDate() - forecastStartDate.getDay());

            const weekStartDate = new Date(firstSunday);
            weekStartDate.setDate(firstSunday.getDate() + index * 7);
            weekStartRow[week] = weekStartDate.toISOString().split('T')[0];
        });
        rows.push(weekStartRow);

        // Past week date selection row
        const pastWeekRow: TableRow = {metric: 'Past Week Date'};
        weeks.forEach((week, index) => {
            pastWeekRow[week] = `date_picker_${index}`;
        });
        rows.push(pastWeekRow);

        // First row: actual_value of each past week
        const actualValueRow: TableRow = {metric: 'Corrected Demand (Past Week)'};
        weeks.forEach((week, index) => {
            const pastWeekResponse = pastWeekDataArray[index];
            if (!pastWeekResponse?.records || !Array.isArray(pastWeekResponse.records)) {
                actualValueRow[week] = hasInitialLoad ? 'Loading...' : '';
                return;
            }

            // Sum actual_value across all records in the week
            const totalActualValue = pastWeekResponse.records.reduce((sum: number, record: any) => {
                return sum + (record.insights?.actual_value || 0);
            }, 0);

            actualValueRow[week] = totalActualValue || 'Loading...';
        });
        rows.push(actualValueRow);

        // Feature group rows: aggregated features minus past week values
        Object.entries(featureMapping).forEach(([groupName, features]) => {
            // Convert group names to normal English
            const displayName = groupName
                .replace(/_/g, ' ')
                .toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());

            const featureRow: TableRow = {metric: displayName};
            weeks.forEach((week, index) => {
                const currentWeekResponse = weeklyData[index];
                const pastWeekResponse = pastWeekDataArray[index];

                if (
                    !currentWeekResponse?.records ||
                    !Array.isArray(currentWeekResponse.records) ||
                    !pastWeekResponse?.records ||
                    !Array.isArray(pastWeekResponse.records)
                ) {
                    featureRow[week] = hasInitialLoad ? 'Loading...' : '';
                } else {
                    // Calculate SHAP differences correctly: aggregate daily differences, then sum by feature group
                    const groupDifference = currentWeekResponse.records.reduce(
                        (weekSum: number, currentRecord: any, recordIndex: number) => {
                            // Find corresponding past week record for the same date
                            const correspondingPastRecord = Array.isArray(pastWeekResponse.records)
                                ? pastWeekResponse.records.find(
                                      (pastRecord: any) => pastRecord.balanceDate === currentRecord.balanceDate
                                  ) || pastWeekResponse.records[recordIndex]
                                : null;

                            if (!correspondingPastRecord) return weekSum;

                            const currentInsights = currentRecord.insights || {};
                            const pastInsights = correspondingPastRecord.insights || {};

                            // Calculate feature group difference for this record
                            const recordGroupDiff = features.reduce((featureSum, feature) => {
                                const shapValue = currentInsights[`shap_${feature}`] || 0;
                                const inSampleShapValue = pastInsights[`in_sample_shap_${feature}`] || 0;
                                return featureSum + (shapValue - inSampleShapValue);
                            }, 0);

                            return weekSum + recordGroupDiff;
                        },
                        0
                    );

                    featureRow[week] = {
                        value: groupDifference,
                        groupName,
                        weekIndex: index,
                        features,
                    };
                }
            });
            rows.push(featureRow);
        });

        // Check for ungrouped features and add "Other Features" row
        const allGroupedFeatures = new Set(Object.values(featureMapping).flat());
        const allFeatures = new Set<string>();

        // Collect all SHAP features from the API responses
        weeklyData.forEach((weekResponse) => {
            if (weekResponse?.records && Array.isArray(weekResponse.records)) {
                weekResponse.records.forEach((record) => {
                    if (record.insights) {
                        Object.keys(record.insights).forEach((key) => {
                            if (key.startsWith('shap_')) {
                                const feature = key.replace('shap_', '');
                                allFeatures.add(feature);
                            }
                        });
                    }
                });
            }
        });

        // Find ungrouped features
        const ungroupedFeatures = Array.from(allFeatures).filter((feature) => !allGroupedFeatures.has(feature));

        if (ungroupedFeatures.length > 0) {
            const otherFeaturesRow: TableRow = {metric: 'Other Features'};
            weeks.forEach((week, index) => {
                const currentWeekResponse = weeklyData[index];
                const pastWeekResponse = pastWeekDataArray[index];

                if (
                    !currentWeekResponse?.records ||
                    !Array.isArray(currentWeekResponse.records) ||
                    !pastWeekResponse?.records ||
                    !Array.isArray(pastWeekResponse.records)
                ) {
                    otherFeaturesRow[week] = hasInitialLoad ? 'Loading...' : '';
                } else {
                    // Calculate SHAP differences for ungrouped features
                    const groupDifference = currentWeekResponse.records.reduce(
                        (weekSum: number, currentRecord: any, recordIndex: number) => {
                            const correspondingPastRecord = Array.isArray(pastWeekResponse.records)
                                ? pastWeekResponse.records.find(
                                      (pastRecord: any) => pastRecord.balanceDate === currentRecord.balanceDate
                                  ) || pastWeekResponse.records[recordIndex]
                                : null;

                            if (!correspondingPastRecord) return weekSum;

                            const currentInsights = currentRecord.insights || {};
                            const pastInsights = correspondingPastRecord.insights || {};

                            const recordGroupDiff = ungroupedFeatures.reduce((featureSum, feature) => {
                                const shapValue = currentInsights[`shap_${feature}`] || 0;
                                const inSampleShapValue = pastInsights[`in_sample_shap_${feature}`] || 0;
                                return featureSum + (shapValue - inSampleShapValue);
                            }, 0);

                            return weekSum + recordGroupDiff;
                        },
                        0
                    );

                    otherFeaturesRow[week] = {
                        value: groupDifference,
                        groupName: 'Other Features',
                        weekIndex: index,
                        features: ungroupedFeatures,
                    };
                }
            });
            rows.push(otherFeaturesRow);
        }

        // Model error row: in_sample_forecast_value - actual_value from past week
        const modelErrorRow: TableRow = {metric: 'Model Error'};
        weeks.forEach((week, index) => {
            const pastWeekResponse = pastWeekDataArray[index];

            if (!pastWeekResponse?.records || !Array.isArray(pastWeekResponse.records)) {
                modelErrorRow[week] = hasInitialLoad ? 'Loading...' : '';
                return;
            }

            // Sum in_sample_forecast_value across all records in past week
            const totalInSampleForecastValue = pastWeekResponse.records.reduce((sum: number, record: any) => {
                return sum + (record.insights?.in_sample_forecast_value || 0);
            }, 0);

            // Sum actual_value across all records in past week
            const totalActualValue = pastWeekResponse.records.reduce((sum: number, record: any) => {
                return sum + (record.insights?.actual_value || 0);
            }, 0);

            const modelError = totalInSampleForecastValue - totalActualValue;

            // Create object with popover data for Model Error
            modelErrorRow[week] = {
                value: modelError,
                groupName: 'Model Error',
                weekIndex: index,
                features: [], // Not used for Model Error
                isModelError: true,
                inSampleForecast: totalInSampleForecastValue,
                actualValue: totalActualValue,
            };
        });
        rows.push(modelErrorRow);

        // Final row: forecast_value of target week
        const forecastRow: TableRow = {metric: 'Model Inference (Target Week)'};
        weeks.forEach((week, index) => {
            const currentWeekResponse = weeklyData[index];
            if (!currentWeekResponse?.records || !Array.isArray(currentWeekResponse.records)) {
                forecastRow[week] = hasInitialLoad ? 'Loading...' : '';
                return;
            }

            // Sum forecast_value across all records in the week
            const totalForecastValue = currentWeekResponse.records.reduce((sum: number, record: any) => {
                return sum + (record.insights?.forecast_value || 0);
            }, 0);

            forecastRow[week] = totalForecastValue || 'Loading...';
        });
        rows.push(forecastRow);

        // Post-process step contribution rows
        const postProcessSteps = [
            {step: 'zip_mapping', name: 'Zip Mapping'},
            {step: 'demand_curve', name: 'Demand Curve'},
            {step: 'equalize_zero_orders_and_units', name: 'Equalize Zero Orders And Units'},
            {step: 'store_hour_normalization', name: 'Store Hour Normalization'},
            {step: 'inference_publishing', name: 'Inference Publishing'},
        ];

        postProcessSteps.forEach(({step, name}) => {
            const stepRow: TableRow = {metric: name};
            weeks.forEach((week, index) => {
                const currentWeekResponse = weeklyData[index];
                if (!currentWeekResponse?.records || !Array.isArray(currentWeekResponse.records)) {
                    stepRow[week] = hasInitialLoad ? 'Loading...' : '';
                    return;
                }

                const totalContribution = currentWeekResponse.records.reduce((sum: number, record: any) => {
                    return sum + (record.insights?.[`${step}_contribution`] || 0);
                }, 0);

                stepRow[week] = {
                    value: totalContribution,
                    groupName: name,
                    weekIndex: index,
                    features: [],
                    isPostProcess: true,
                    step: step,
                };
            });
            rows.push(stepRow);
        });

        // Final baseline forecast row
        const baselineForecastRow: TableRow = {metric: 'Baseline Forecast (Target Week)'};
        weeks.forEach((week, index) => {
            const currentWeekResponse = weeklyData[index];
            if (!currentWeekResponse?.records || !Array.isArray(currentWeekResponse.records)) {
                baselineForecastRow[week] = hasInitialLoad ? 'Loading...' : '';
                return;
            }

            // Try final_forecast first, fallback to forecast_value if final_forecast doesn't exist
            const totalFinalForecast = currentWeekResponse.records.reduce((sum: number, record: any) => {
                const finalForecast = record.insights?.final_forecast;
                const forecastValue = record.insights?.forecast_value;
                return sum + (finalForecast !== undefined ? finalForecast : forecastValue || 0);
            }, 0);

            baselineForecastRow[week] = totalFinalForecast || 'Loading...';
        });
        rows.push(baselineForecastRow);

        return rows;
    }, [weeklyData, pastWeekDataArray, featureMapping, pastWeekDates, hasInitialLoad]);

    const weeks = Array.from({length: 5}, (_, index) => `Week ${index + 1}`);

    const updatePastWeekDate = (weekIndex: number, date: string) => {
        const newDates = [...pastWeekDates];
        newDates[weekIndex] = date;
        setPastWeekDates(newDates);
    };

    const formatValue = (value: string | number | any, weekIndex?: number, metric?: string): React.ReactNode => {
        // Handle date picker cells
        if (typeof value === 'string' && value.startsWith('date_picker_')) {
            const index = parseInt(value.split('_')[2]);
            return (
                <Input
                    value={pastWeekDates[index] || ''}
                    onChange={({detail}) => updatePastWeekDate(index, detail.value)}
                    placeholder="YYYY-MM-DD"
                />
            );
        }

        // Handle loading state
        if (value === 'Loading...') {
            return <span style={{color: 'gray'}}>Loading...</span>;
        }

        // Handle empty cell
        if (value === '') {
            return '';
        }

        // Handle feature group objects with popover
        if (typeof value === 'object' && value !== null && 'value' in value) {
            const numValue = value.value;
            const formattedValue = formatNumber(numValue);
            const color = numValue >= 0 ? 'green' : 'red';
            const displayValue = numValue >= 0 ? `+${formattedValue}` : formattedValue;

            // Special handling for post-process steps
            if (value.isPostProcess) {
                const currentWeekResponse = weeklyData[value.weekIndex];
                if (!currentWeekResponse?.records || !Array.isArray(currentWeekResponse.records)) {
                    return <span style={{color}}>{displayValue}</span>;
                }

                const modalContent = (
                    <Table
                        columnDefinitions={[
                            {id: 'date', header: 'Date', cell: (item: any) => item.date},
                            {id: 'value', header: 'Value', cell: (item: any) => formatNumber(item.value)},
                        ]}
                        items={currentWeekResponse.records.map((record: any, idx: number) => ({
                            date: record.balanceDate?.split('T')[0] || `Day ${idx + 1}`,
                            value: record.insights?.[`${value.step}_contribution`] || 0,
                        }))}
                        empty={<Box textAlign="center">No daily data available</Box>}
                    />
                );

                return (
                    <Button
                        variant="inline-link"
                        onClick={() => {
                            setModalData({
                                title: `${value.groupName} - Daily Breakdown`,
                                content: modalContent,
                            });
                            setModalVisible(true);
                        }}
                    >
                        <span style={{color}}>{displayValue}</span>
                    </Button>
                );
            }

            // Special handling for Model Error modal
            if (value.isModelError) {
                const modalContent = (
                    <Box padding="m">
                        <Table
                            columnDefinitions={[
                                {id: 'component', header: 'Component', cell: (item: any) => item.component},
                                {id: 'value', header: 'Value', cell: (item: any) => formatNumber(item.value)},
                            ]}
                            items={[
                                {component: 'In-Sample Forecast Value', value: value.inSampleForecast},
                                {component: 'Corrected Demand (Actual Value)', value: value.actualValue},
                                {component: 'Model Error (Difference)', value: value.value},
                            ]}
                            empty={<Box textAlign="center">No data available</Box>}
                        />
                        <Box margin={{top: 's'}}>
                            <strong>Calculation:</strong> {formatNumber(value.inSampleForecast)} - {formatNumber(value.actualValue)}{' '}
                            = {formatNumber(value.value)}
                        </Box>
                    </Box>
                );

                return (
                    <Button
                        variant="inline-link"
                        onClick={() => {
                            setModalData({
                                title: 'Model Error Breakdown',
                                content: modalContent,
                            });
                            setModalVisible(true);
                        }}
                    >
                        <span style={{color}}>{displayValue}</span>
                    </Button>
                );
            }

            // Regular feature group modal
            const featureDetails = getFeatureDetails(value.groupName, value.weekIndex, value.features);

            // Handle case where featureDetails might be empty array
            const aggregatedData = Array.isArray(featureDetails) ? [] : featureDetails.aggregatedData || [];
            const modalContent = (
                <Box>
                    <Table
                        columnDefinitions={[
                            {id: 'feature', header: 'Feature', cell: (item: any) => item.feature},
                            {
                                id: 'shap',
                                header: 'SHAP Value',
                                cell: (item: any) => (
                                    <Popover
                                        size="medium"
                                        position="top"
                                        triggerType="custom"
                                        dismissButton={false}
                                        content={
                                            <Box padding="s">
                                                <Header variant="h3">Daily SHAP Values</Header>
                                                {item.dailyValues.map((val: number, idx: number) => (
                                                    <div key={idx}>
                                                        Day {idx + 1}: {formatNumber(val)}
                                                    </div>
                                                ))}
                                            </Box>
                                        }
                                    >
                                        <Button variant="inline-link" onClick={() => {}}>
                                            {item.shap != null ? formatNumber(item.shap) : 'N/A'}
                                        </Button>
                                    </Popover>
                                ),
                            },
                            {
                                id: 'inSample',
                                header: 'In-Sample SHAP',
                                cell: (item: any) => (
                                    <Popover
                                        size="medium"
                                        position="top"
                                        triggerType="custom"
                                        dismissButton={false}
                                        content={
                                            <Box padding="s">
                                                <Header variant="h3">Daily In-Sample SHAP Values</Header>
                                                {item.dailyInSampleValues.map((val: number, idx: number) => (
                                                    <div key={idx}>
                                                        Day {idx + 1}: {formatNumber(val)}
                                                    </div>
                                                ))}
                                            </Box>
                                        }
                                    >
                                        <Button variant="inline-link" onClick={() => {}}>
                                            {item.inSample != null ? formatNumber(item.inSample) : 'N/A'}
                                        </Button>
                                    </Popover>
                                ),
                            },
                            {
                                id: 'difference',
                                header: 'Difference',
                                cell: (item: any) => {
                                    const diff = item.difference;
                                    return (
                                        <Popover
                                            size="medium"
                                            position="top"
                                            triggerType="custom"
                                            dismissButton={false}
                                            content={
                                                <Box padding="s">
                                                    <Header variant="h3">Daily Differences</Header>
                                                    {item.dailyDifferences.map((val: number, idx: number) => (
                                                        <div key={idx} style={{color: val >= 0 ? 'green' : 'red'}}>
                                                            Day {idx + 1}: {formatNumber(val)}
                                                        </div>
                                                    ))}
                                                </Box>
                                            }
                                        >
                                            <Button variant="inline-link" onClick={() => {}}>
                                                <span style={{color: diff >= 0 ? 'green' : 'red'}}>{formatNumber(diff)}</span>
                                            </Button>
                                        </Popover>
                                    );
                                },
                            },
                        ]}
                        items={aggregatedData}
                        empty={<Box textAlign="center">No feature data available</Box>}
                    />
                </Box>
            );

            return (
                <Button
                    variant="inline-link"
                    onClick={() => {
                        setModalData({
                            title: `${value.groupName} Details`,
                            content: modalContent,
                        });
                        setModalVisible(true);
                    }}
                >
                    <span style={{color}}>{displayValue}</span>
                </Button>
            );
        }

        if (typeof value === 'number') {
            const boldRows = ['Corrected Demand (Past Week)', 'Model Inference (Target Week)', 'Baseline Forecast (Target Week)'];
            const isBoldRow = metric && boldRows.includes(metric);

            if (isBoldRow) {
                // No color, no +/- sign for bold rows, but make them clickable for daily breakdown
                return (
                    <Button
                        variant="inline-link"
                        onClick={() => {
                            const dailyBreakdown = getDailyBreakdownForBoldRow(metric, weekIndex || 0);
                            setModalData({
                                title: `${metric} - Daily Breakdown`,
                                content: (
                                    <Table
                                        columnDefinitions={[
                                            {id: 'date', header: 'Date', cell: (item: any) => item.date},
                                            {id: 'value', header: 'Value', cell: (item: any) => formatNumber(item.value)},
                                        ]}
                                        items={dailyBreakdown}
                                        empty={<Box textAlign="center">No daily data available</Box>}
                                    />
                                ),
                            });
                            setModalVisible(true);
                        }}
                    >
                        {formatNumber(value)}
                    </Button>
                );
            } else {
                // Colored with +/- sign for other rows
                const formattedValue = formatNumber(value);
                if (value >= 0) {
                    return <span style={{color: 'green'}}>+{formattedValue}</span>;
                } else {
                    return <span style={{color: 'red'}}>{formattedValue}</span>;
                }
            }
        }
        return value;
    };

    const getDailyBreakdownForBoldRow = (metric: string, weekIndex: number) => {
        const currentWeekResponse = weeklyData[weekIndex];
        const pastWeekResponse = pastWeekDataArray[weekIndex];

        if (metric === 'Corrected Demand (Past Week)') {
            if (!pastWeekResponse?.records || !Array.isArray(pastWeekResponse.records)) return [];
            return pastWeekResponse.records.map((record: any, idx: number) => ({
                date: record.balanceDate?.split('T')[0] || `Day ${idx + 1}`,
                value: record.insights?.actual_value || 0,
            }));
        }

        if (metric === 'Model Inference (Target Week)') {
            if (!currentWeekResponse?.records || !Array.isArray(currentWeekResponse.records)) return [];
            return currentWeekResponse.records.map((record: any, idx: number) => ({
                date: record.balanceDate?.split('T')[0] || `Day ${idx + 1}`,
                value: record.insights?.forecast_value || 0,
            }));
        }

        if (metric === 'Baseline Forecast (Target Week)') {
            if (!currentWeekResponse?.records || !Array.isArray(currentWeekResponse.records)) return [];
            return currentWeekResponse.records.map((record: any, idx: number) => ({
                date: record.balanceDate?.split('T')[0] || `Day ${idx + 1}`,
                value: record.insights?.final_forecast || record.insights?.forecast_value || 0,
            }));
        }

        return [];
    };

    const getFeatureDetails = (groupName: string, weekIndex: number, features: string[]) => {
        const currentWeekResponse = weeklyData[weekIndex];
        const pastWeekResponse = pastWeekDataArray[weekIndex];

        if (
            !currentWeekResponse?.records ||
            !Array.isArray(currentWeekResponse.records) ||
            !pastWeekResponse?.records ||
            !Array.isArray(pastWeekResponse.records)
        ) {
            return {aggregatedData: []};
        }

        // Create daily data for each feature
        const dailyData: any[] = [];
        const aggregatedData: any[] = [];

        // Process daily records
        currentWeekResponse.records.forEach((currentRecord: any, recordIndex: number) => {
            const correspondingPastRecord = Array.isArray(pastWeekResponse.records)
                ? pastWeekResponse.records.find((pastRecord: any) => pastRecord.balanceDate === currentRecord.balanceDate) ||
                  pastWeekResponse.records[recordIndex]
                : null;

            if (!correspondingPastRecord) return;

            const date = currentRecord.balanceDate?.split('T')[0] || `Day ${recordIndex + 1}`;

            features.forEach((feature) => {
                const shapValue = currentRecord.insights?.[`shap_${feature}`] || 0;
                const inSampleValue = correspondingPastRecord.insights?.[`in_sample_shap_${feature}`] || 0;

                dailyData.push({
                    date,
                    feature,
                    shap: shapValue,
                    inSample: inSampleValue,
                    difference: shapValue - inSampleValue,
                });
            });
        });

        // Create aggregated data with daily breakdown
        features.forEach((feature) => {
            // Collect daily values for this feature
            const dailyShapValues: number[] = [];
            const dailyInSampleValues: number[] = [];
            const dailyDifferences: number[] = [];

            if (!Array.isArray(currentWeekResponse.records)) return;

            currentWeekResponse.records.forEach((currentRecord: any, recordIndex: number) => {
                const correspondingPastRecord = Array.isArray(pastWeekResponse.records)
                    ? pastWeekResponse.records.find((pastRecord: any) => pastRecord.balanceDate === currentRecord.balanceDate) ||
                      pastWeekResponse.records[recordIndex]
                    : null;

                if (correspondingPastRecord) {
                    const shapValue = currentRecord.insights?.[`shap_${feature}`] || 0;
                    const inSampleValue = correspondingPastRecord.insights?.[`in_sample_shap_${feature}`] || 0;

                    dailyShapValues.push(shapValue);
                    dailyInSampleValues.push(inSampleValue);
                    dailyDifferences.push(shapValue - inSampleValue);
                }
            });

            const totalShapValue = dailyShapValues.reduce((sum, val) => sum + val, 0);
            const totalInSampleValue = dailyInSampleValues.reduce((sum, val) => sum + val, 0);

            aggregatedData.push({
                feature,
                shap: totalShapValue,
                inSample: totalInSampleValue,
                difference: totalShapValue - totalInSampleValue,
                dailyValues: dailyShapValues,
                dailyInSampleValues: dailyInSampleValues,
                dailyDifferences: dailyDifferences,
            });
        });

        // Sort aggregated data by absolute difference
        aggregatedData.sort((a, b) => {
            const diffA = Math.abs(a.difference);
            const diffB = Math.abs(b.difference);
            return diffB - diffA;
        });

        return {aggregatedData};
    };

    return (
        <Table
            data-testid="lgbm-explainer-table"
            stickyHeader
            stripedRows
            stickyColumns={{first: 1}}
            columnDefinitions={[
                {
                    id: 'metric',
                    header: '',
                    isRowHeader: true,
                    cell: (item: TableRow) => {
                        const boldRows = [
                            'Corrected Demand (Past Week)',
                            'Model Inference (Target Week)',
                            'Baseline Forecast (Target Week)',
                        ];
                        const isBold = boldRows.includes(item.metric);
                        return <span style={{fontWeight: isBold ? 'bold' : 'normal'}}>{item.metric}</span>;
                    },
                    width: 200,
                },
                ...weeks.map((week, weekIndex) => ({
                    id: week,
                    header: week,
                    cell: (item: TableRow) => formatValue(item[week], weekIndex, item.metric),
                    width: 180,
                })),
            ]}
            header={<Header>Weekly Feature SHAP Contribution Versus Past Weeks & Post Processing Contribution</Header>}
            items={tableData}
            empty={
                <Box textAlign="center" color="inherit">
                    <b>No data available</b>
                    <Box variant="p" color="inherit">
                        Please apply filters to load data
                    </Box>
                </Box>
            }
        />
    );
}
